import { z } from 'zod';
/**
 * Contrato da fila entre a API e o worker.
 *
 * A API produz e o worker consome; sem um lugar só definindo nome de fila,
 * nome de tarefa e formato do payload, os dois lados divergiriam num rename
 * silencioso e o trabalho pararia de ser entregue sem ninguém ver erro.
 */
/**
 * Fila única do projeto.
 *
 * O nome fala em cadências porque a varredura delas foi o primeiro trabalho a
 * existir, e hoje a fila também carrega a ingestão de leads da Meta. Mantido
 * assim de propósito: renomear criaria uma fila nova no Redis e deixaria o
 * agendador da fila antiga órfão, trocando um nome impreciso por um trabalho
 * que some. Trabalhos futuros entram aqui como tarefas novas.
 */
export const FILA_DE_TRABALHOS = 'reducee-cadencias';
export const TAREFA_VARREDURA_DE_CADENCIAS = 'encerrar-cadencias-esgotadas';
export const TAREFA_INGESTAO_DE_LEAD = 'processar-aviso-de-lead';
/**
 * Busca por relógio no Lead Center da Meta.
 *
 * Existe porque a entrega em tempo real não acontece: as permissões do
 * aplicativo estão em Acesso Padrão, que só dispara o webhook para quem tem
 * papel no app, então lead de gente que clicou no anúncio nunca é entregue.
 * Ler o Lead Center funciona, e esta tarefa é essa leitura pendurada no
 * relógio.
 *
 * É remendo declarado, e não a entrada que se quer: o Lead deveria aparecer no
 * instante do envio, e aqui ele aparece no próximo ciclo. Some quando a Meta
 * liberar o Acesso Avançado e o webhook passar a entregar.
 */
export const TAREFA_BUSCA_NO_LEAD_CENTER = 'buscar-no-lead-center';
/**
 * Espelho dos Anúncios da conta, por relógio.
 *
 * A parede lê de uma cópia guardada aqui, e não da Meta a cada abertura: são
 * 114 anúncios e uma imagem cada, e consultar na abertura deixaria a tela
 * dependente de a Meta estar no ar justamente quando alguém quer olhar para o
 * que os anúncios renderam.
 *
 * Hífen, e nunca dois-pontos: o BullMQ usa o caractere como separador das
 * chaves dele no Redis e recusa o trabalho na entrada, e essa recusa só
 * aparece em log.
 */
export const TAREFA_SINCRONIZACAO_DE_ANUNCIOS = 'sincronizar-anuncios-da-meta';
/**
 * Envio ao B2Cor dos Leads que ainda não foram.
 *
 * Roda por relógio e também logo depois de a ingestão criar um Lead: o
 * relógio é a rede que pega o que o B2Cor recusou por fora do ar, e o disparo
 * imediato é o que faz o Lead aparecer lá no instante em que aparece aqui.
 * Mesmo hífen, pela mesma razão das de cima.
 */
export const TAREFA_ENVIO_AO_B2COR = 'enviar-leads-ao-b2cor';
/**
 * Payload da ingestão de um Aviso.
 *
 * Validado na saída da fila porque ali ele é dado de fora: chega do Redis, que
 * pode ter trabalho enfileirado por uma versão anterior do código, por outro
 * processo, ou simplesmente corrompido. Um payload que não confere é descartado
 * sem derrubar o consumidor.
 *
 * Carrega só o identificador do lead. O resto o worker lê do banco: repetir
 * dados aqui abriria espaço para a fila e a base discordarem, e obrigaria a
 * confiar na fila para decidir o que gravar.
 */
export const esquemaDaIngestaoDeLead = z.object({
    leadgenId: z.string().min(1).max(64),
});
/**
 * Identificador do trabalho na fila, derivado do lead.
 *
 * É a chave de idempotência da fila: a Meta reentrega o que não foi confirmado,
 * e sem isso a mesma entrega viraria dois trabalhos. Com ele, o segundo é
 * recusado pelo próprio BullMQ, antes de chegar ao banco.
 *
 * Separado por hífen, nunca por dois-pontos: o BullMQ recusa o caractere em
 * identificador próprio, porque é o separador das chaves dele no Redis — a
 * mesma razão que já valia para o nome da fila. Com dois-pontos, o trabalho é
 * recusado na hora de entrar, e o lead só é alcançado pela varredura, com o
 * atraso dela.
 *
 * O prefixo não é enfeite: o BullMQ também recusa identificador só de dígitos,
 * para não colidir com os que ele gera sozinho — e o identificador da Meta é
 * exatamente isso.
 */
export function idDoTrabalhoDeIngestao(leadgenId) {
    return `${TAREFA_INGESTAO_DE_LEAD}-${leadgenId}`;
}
//# sourceMappingURL=trabalhos-da-fila.js.map